/**
 * Селектор активного tenant'а у хедері (Порція 3.0.5.1b, спец розд. 2.2).
 *
 * Поведінка:
 *  - Кнопка з назвою поточного tenant'а та шевроном.
 *  - При кліку — випадаючий список усіх ACTIVE membership user'а.
 *  - Вибір іншого tenant'а → активуємо + queryClient.clear() + повний
 *    reload, як вимагає спец (гарантоване перечитування /auth/me, прав).
 *  - Якщо user має тільки 1 tenant — показуємо назву без dropdown'а.
 */
import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyTenants, type MyTenantItem } from '@/api/auth';
import { useAuth } from '@/auth/store';

export default function TenantSwitcher() {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const tenants = useQuery<MyTenantItem[]>({
    queryKey: ['my-tenants'],
    queryFn: getMyTenants,
    // Кешуємо довше — список tenant'ів рідко змінюється.
    staleTime: 5 * 60 * 1000,
  });

  // Закривати dropdown при кліку поза ним.
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  const list = tenants.data ?? [];
  const active = list.find((t) => t.id === activeTenantId);

  function switchTo(tenantId: string) {
    if (tenantId === activeTenantId) {
      setOpen(false);
      return;
    }
    setActiveTenant(tenantId);
    queryClient.clear();
    // Full reload за спецою — гарантоване перечитування /auth/me, прав,
    // меню. Без цього cached дані попереднього tenant можуть просочитися.
    window.location.reload();
  }

  if (!activeTenantId || list.length === 0) {
    return null; // Не показуємо до моменту, коли є вибраний tenant.
  }

  // Якщо лиш один tenant — просто показуємо назву без dropdown'а.
  if (list.length === 1) {
    return (
      <span
        style={{
          fontSize: '0.875rem',
          color: 'var(--text-secondary)',
          padding: '0.3rem 0.6rem',
        }}
      >
        {active?.name ?? '...'}
      </span>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      data-testid="tenant-switcher"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          background: 'var(--bg-soft-2)',
          fontWeight: 700,
          minWidth: 160,
          textAlign: 'left',
        }}
      >
        {active?.name ?? 'Виберіть клієнта'}
        <span style={{ marginLeft: 8, opacity: 0.6 }}>▾</span>
      </button>
      {open && (
        <div
          className="dropdown-menu"
          role="listbox"
          style={{ top: '100%', right: 0, marginTop: 4, minWidth: 240 }}
        >
          {list.map((t) => (
            <div
              key={t.id}
              role="option"
              aria-selected={t.id === activeTenantId}
              className={`item ${t.id === activeTenantId ? 'active' : ''}`}
              onClick={() => switchTo(t.id)}
            >
              <div style={{ fontWeight: 700 }}>{t.name}</div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)',
                }}
              >
                {t.display_role}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
